import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { CLASS_ACTIVE, CLASS_HIDDEN } from '../tools/constants/classes';
import { EVT_VARIANT_CHANGED } from './product/variant-selector';

import { openContentDrawer } from './content-drawer';

const SELECTOR_SOLDOUT_CONTAINER = '[data-sold-out-notify-wrapper]';
const SELECTOR_SUBMIT_MESSAGE_DEFAUL = '[data-notify-message-default]';
const SELECTOR_SUBMIT_MESSAGE_SUCCESS = '[data-notify-message-success]';
const SELECTOR_SOLDOUT_EMAIL = '[data-sold-out-email]';
const SELECTOR_SOLDOUT_PHONE = '[data-sold-out-phone]';
const SELECTOR_BIS_VARIANT_INPUT = '[data-bis-variant]';
const SELECTOR_SOLDOUT_SUBMIT_BUTTON = '[data-sold-out-notify-submit]';
const SELECTOR_SOLDOUT_NOTIFY_ERROR = '[data-notify-error]';
const SELECTOR_VARIANT_SELECTOR_DATA = '[data-variant-selector-data]';

const ATTR_KEY = 'data-key';
const KLAVIYO_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_PROFILES_URL = 'https://a.klaviyo.com/client/profiles/';
const KLAVIYO_REVISION = '2024-07-15';
const PLATFORM = 'shopify';

let notifyButtons: NodeListOf<HTMLButtonElement>;

export function initBackInStockForm() {
  notifyButtons = document.querySelectorAll('[data-notify]');

  notifyButtons.forEach(button => {
    if (button.hasAttribute('data-bis-trigger-initialized')) return;
    
    button.addEventListener('click', handleNotifyButtonClick);
    button.setAttribute('data-bis-trigger-initialized', 'true');
  });

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.matches(SELECTOR_SOLDOUT_SUBMIT_BUTTON) || target.closest(SELECTOR_SOLDOUT_SUBMIT_BUTTON)) {
      const button = target.matches(SELECTOR_SOLDOUT_SUBMIT_BUTTON) 
        ? target as HTMLElement 
        : target.closest(SELECTOR_SOLDOUT_SUBMIT_BUTTON) as HTMLElement;
      if (button) {
        onButtonSubmitSoldOutNotify(e);
      }
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target as HTMLFormElement;
    if (form && form.matches('[data-sold-out-notify]')) {
      onButtonSubmitSoldOutNotify(e);
    }
  });
}

function handleNotifyButtonClick(e: Event) {
  e.preventDefault();
  e.stopPropagation();

  const button = e.currentTarget as HTMLButtonElement;
  const drawer = document.querySelector('[data-content-drawer="back-in-stock"]') as HTMLElement;
  
  if (!drawer) return;

  const productForm = button.closest('[data-product-form]') as HTMLElement;
  if (productForm) {
    updateDrawerWithVariantData(drawer, productForm);
    
    const updateVariantFromEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.variant) {
        const variantId = customEvent.detail.variant.id;
        const variantIdInput = drawer.querySelector(SELECTOR_BIS_VARIANT_INPUT) as HTMLInputElement;
        if (variantIdInput && variantId) {
          variantIdInput.value = variantId.toString();
        }
      } else {
        updateDrawerWithVariantData(drawer, productForm);
      }
    };
    
    productForm.addEventListener(EVT_VARIANT_CHANGED, updateVariantFromEvent);
    
    const variantSelectorContainer = productForm.querySelector('[data-variant-selector]');
    const variantSelector = variantSelectorContainer 
      ? (variantSelectorContainer.querySelector('[name="id"]') as HTMLInputElement | HTMLSelectElement)
      : (productForm.querySelector('[name="id"]') as HTMLInputElement | HTMLSelectElement);
      
    if (variantSelector) {
      const updateVariant = () => {
        updateDrawerWithVariantData(drawer, productForm);
      };
      
      variantSelector.addEventListener('change', updateVariant);
    }
  }

  if (productForm) {
    updateDrawerWithVariantData(drawer, productForm);
  }
  
  openContentDrawer(drawer);
  
  setTimeout(() => {
    const submitButtons = drawer.querySelectorAll(SELECTOR_SOLDOUT_SUBMIT_BUTTON);
    submitButtons.forEach(btn => {
      if (!btn.hasAttribute('data-bis-submit-initialized')) {
        btn.addEventListener('click', onButtonSubmitSoldOutNotify);
        btn.setAttribute('data-bis-submit-initialized', 'true');
      }
    });
    
    if (productForm) {
      updateDrawerWithVariantData(drawer, productForm);
    }
  }, 100);
}

function updateDrawerWithVariantData(drawer: HTMLElement, productForm: HTMLElement) {
  const variantSelectorContainer = productForm.querySelector('[data-variant-selector]');
  
  let variantInput: HTMLInputElement | HTMLSelectElement | null = null;
  
  if (variantSelectorContainer) {
    variantInput = variantSelectorContainer.querySelector('input[name="id"]') as HTMLInputElement;
    if (!variantInput) {
      variantInput = variantSelectorContainer.querySelector('select[name="id"]') as HTMLSelectElement;
    }
  } else {
    variantInput = productForm.querySelector('input[name="id"]') as HTMLInputElement;
    if (!variantInput) {
      variantInput = productForm.querySelector('select[name="id"]') as HTMLSelectElement;
    }
  }
  
  
  let variantId = '';
  if (variantInput) {
    variantId = variantInput.value || '';
  }
  
  // If still no variant ID, try to get from FormData
  if (!variantId && productForm.tagName === 'FORM') {
    const formData = new FormData(productForm as HTMLFormElement);
    variantId = formData.get('id')?.toString() || '';
  }
  
  // If still no variant ID, try to get from URL query parameter
  if (!variantId) {
    const urlParams = new URLSearchParams(window.location.search);
    variantId = urlParams.get('variant') || '';
  }
  
  if (!variantId && variantSelectorContainer) {
    const variantDataElement = variantSelectorContainer.querySelector(SELECTOR_VARIANT_SELECTOR_DATA) as HTMLScriptElement;
    if (variantDataElement) {
      try {
        const productData = JSON.parse(variantDataElement.innerHTML);
        if (productData.variants && productData.variants.length > 0) {
          const selectedVariant = productData.variants.find((v: any) => {
            if (variantInput && variantInput.tagName === 'SELECT') {
              return v.id.toString() === (variantInput as HTMLSelectElement).value;
            }
            return false;
          });
          
          if (selectedVariant) {
            variantId = selectedVariant.id.toString();
          } else {
            variantId = productData.variants[0].id.toString();
          }
        }
      } catch (e) {
        console.error('Error parsing variant selector data:', e);
      }
    }
  }
  
  // If still no variant ID and we have a select, try to get the first selected option
  if (!variantId && variantInput && variantInput.tagName === 'SELECT') {
    const select = variantInput as HTMLSelectElement;
    if (select.selectedIndex > 0) {
      variantId = select.options[select.selectedIndex].value;
    } else if (select.options.length > 1) {
      for (let i = 1; i < select.options.length; i++) {
        if (select.options[i].value) {
          variantId = select.options[i].value;
          break;
        }
      }
    }
  }
  
  // Update variant ID in drawer
  const variantIdInput = drawer.querySelector(SELECTOR_BIS_VARIANT_INPUT) as HTMLInputElement;
  if (variantIdInput) {
    if (!variantId && variantIdInput.hasAttribute('value')) {
      const defaultValue = variantIdInput.getAttribute('value');
      if (defaultValue) {
        variantId = defaultValue;
      }
    }
    
    // Only update if we have a variant ID
    if (variantId) {
      variantIdInput.value = variantId;
    } 
  } 
}

function updateKlaviyoProfileWithPhone(companyId: string, email: string, phoneNumber: string): Promise<void> {
  const body = {
    data: {
      type: 'profile',
      attributes: {
        email,
        phone_number: phoneNumber,
      },
    },
  };
  return fetch(`${KLAVIYO_PROFILES_URL}?company_id=${companyId}`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      revision: KLAVIYO_REVISION,
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  }).then((res) => {
    if (res.status !== 202) {
      return res.text().then((text) => {
        try {
          const data = JSON.parse(text);
          const msg = data?.errors?.[0]?.detail || data?.errors?.[0]?.title || text || 'Profile update failed';
          throw new Error(msg);
        } catch (e) {
          if (e instanceof Error) throw e;
          throw new Error(text || 'Profile update failed');
        }
      });
    }
  });
}

type PhoneResult = { valid: true; e164: string } | { valid: false };

function validateAndNormalizePhone(
  raw: string,
  defaultCountry?: string | null
): PhoneResult {
  const trimmed = raw.trim();
  if (!trimmed) return { valid: false };

  if (trimmed.length < 4 || trimmed.length > 20) {
    return { valid: false };
  }

  const country = defaultCountry && defaultCountry.length === 2 ? defaultCountry : undefined;
  const parsed = parsePhoneNumberFromString(trimmed, country as any);
  if (!parsed || !parsed.isValid()) return { valid: false };
  return { valid: true, e164: parsed.format('E.164') };
}

const onButtonSubmitSoldOutNotify = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();
  
  const button = (e.target as HTMLElement).closest(SELECTOR_SOLDOUT_SUBMIT_BUTTON) as HTMLElement || e.currentTarget as HTMLElement;
  const form = button.closest('form') as HTMLFormElement;
  const formWrapper = button.closest(SELECTOR_SOLDOUT_CONTAINER) as HTMLElement;
  
  if (formWrapper) {
    const errorMessage = formWrapper.querySelector(SELECTOR_SOLDOUT_NOTIFY_ERROR) as HTMLElement;
    if (errorMessage) {
      const existingTimeout = (errorMessage as any)._hideTimeout;
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      errorMessage.classList.remove(CLASS_ACTIVE);
      errorMessage.textContent = 'Something went wrong. Please try again.';
    }
  }

  if (!form || !formWrapper) {
    throw 'Invalid input';
  }
  
  const container = formWrapper;
  const KLAVIYO_KEY = container.getAttribute('data-key') || ATTR_KEY;
  if (!KLAVIYO_KEY) {
    console.error('No Klaviyo key found');
    return;
  }
  
  let emailContainer = container.querySelector<HTMLInputElement>(SELECTOR_SOLDOUT_EMAIL);
  if (!emailContainer) {
    console.error('Email container not found');
    return;
  }
  const mainEmail = emailContainer.value;

  const phoneInput = container.querySelector<HTMLInputElement>(SELECTOR_SOLDOUT_PHONE);
  const phoneRaw = phoneInput?.value?.trim() || '';
  const defaultCountry = container.getAttribute('data-phone-default-country');

  let variantContainer = container.querySelector<HTMLInputElement>(SELECTOR_BIS_VARIANT_INPUT);
  if (!variantContainer) {
    console.error('Variant container not found');
    return;
  }
  
  let variantId = variantContainer.getAttribute('value') || variantContainer.value || '';
  
  if (!variantId) {
    const drawer = container.closest('[data-content-drawer="back-in-stock"]') as HTMLElement;
    if (drawer) {
      const productForm = document.querySelector('[data-product-form]') as HTMLElement;
      if (productForm) {
        const variantSelectorContainer = productForm.querySelector('[data-variant-selector]');
        let variantInput: HTMLInputElement | HTMLSelectElement | null = null;
        
        if (variantSelectorContainer) {
          variantInput = variantSelectorContainer.querySelector('input[name="id"]') as HTMLInputElement;
          if (!variantInput) {
            variantInput = variantSelectorContainer.querySelector('select[name="id"]') as HTMLSelectElement;
          }
        } else {
          variantInput = productForm.querySelector('input[name="id"]') as HTMLInputElement;
          if (!variantInput) {
            variantInput = productForm.querySelector('select[name="id"]') as HTMLSelectElement;
          }
        }
        
        if (variantInput) {
          variantId = variantInput.value || '';
          
          if (!variantId && variantSelectorContainer) {
            const variantDataElement = variantSelectorContainer.querySelector('[data-variant-selector-data]') as HTMLScriptElement;
            if (variantDataElement) {
              try {
                const productData = JSON.parse(variantDataElement.innerHTML);
                if (productData.variants && productData.variants.length > 0) {
                  if (variantInput && variantInput.tagName === 'SELECT') {
                    const select = variantInput as HTMLSelectElement;
                    if (select.selectedIndex > 0 && select.options[select.selectedIndex].value) {
                      variantId = select.options[select.selectedIndex].value;
                    }
                  }
                  
                  if (!variantId) {
                    variantId = productData.variants[0].id.toString();
                  }
                }
              } catch (e) {
                console.error('Error parsing variant data:', e);
              }
            }
          }
          
          if (variantId) {
            variantContainer.value = variantId;
            variantContainer.setAttribute('value', variantId);
          }
        }
      }
    }
    
    if (!variantId) {
      const urlParams = new URLSearchParams(window.location.search);
      variantId = urlParams.get('variant') || '';
      if (variantId) {
        variantContainer.value = variantId;
        variantContainer.setAttribute('value', variantId);
      }
    }
  } else {
    if (variantId && !variantContainer.value) {
      variantContainer.value = variantId;
    }
  }
  

  const showError = (message: string) => {
    const errorMessage = container.querySelector(SELECTOR_SOLDOUT_NOTIFY_ERROR) as HTMLElement;
    if (errorMessage) {
      const existingTimeout = (errorMessage as any)._hideTimeout;
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      
      errorMessage.textContent = message;
      errorMessage.classList.add(CLASS_ACTIVE);
      
      // Auto-hide after 5 seconds
      (errorMessage as any)._hideTimeout = setTimeout(() => {
        errorMessage.classList.remove(CLASS_ACTIVE);
        delete (errorMessage as any)._hideTimeout;
      }, 5000);
    }
  };

  let re = /\S+@\S+\.\S+/;

  if (!mainEmail || !re.test(mainEmail)) {
    console.error('Invalid email:', mainEmail);
    showError('Invalid email');
    return;
  }

  let phoneNumber = '';
  if (phoneRaw) {
    const phoneResult = validateAndNormalizePhone(phoneRaw, defaultCountry);
    if (!phoneResult.valid) {
      const phoneErrorMsg =
        container.getAttribute('data-phone-error-message') ||
        'Please enter a valid phone number (e.g. +61 412 345 678).';
      showError(phoneErrorMsg);
      return;
    }
    phoneNumber = phoneResult.e164;
    if (phoneInput) phoneInput.value = phoneNumber;
  }

  if (!variantId) {
    console.error('Invalid variant ID:', variantId);
    showError('Something went wrong. Please try again.');
    return;
  }

  const consentCheckbox = form.querySelector<HTMLInputElement>('input[name="consent"]');
  if (consentCheckbox && !consentCheckbox.checked) {
    showError('Please accept the terms to continue.');
    return;
  }
  
  const profileAttributes: { email: string; phone_number?: string } = {
    email: emailContainer.value,
  };
  if (phoneNumber) {
    profileAttributes.phone_number = phoneNumber;
  }

  const requestBody = {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        profile: {
          data: {
            type: 'profile',
            attributes: profileAttributes,
          },
        },
        channels: phoneNumber ? ['EMAIL', 'SMS'] : ['EMAIL'],
      },
      relationships: {
        variant: {
          data: {
            type: 'catalog-variant',
            id: `$shopify:::$default:::${variantId}`,
          },
        },
      },
    },
  };

  const options = {
    method: 'POST',
    headers: {
      accept: 'application/json',
      revision: KLAVIYO_REVISION,
      'content-type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  const runBackInStockRequest = () =>
    fetch(`${KLAVIYO_URL}?company_id=${KLAVIYO_KEY}`, options);

  const readyPromise: Promise<void> = phoneNumber
    ? updateKlaviyoProfileWithPhone(KLAVIYO_KEY, mainEmail, phoneNumber)
        .then(() => {})
        .catch(() => {
          showError('We will notify you via email. SMS notifications may not be available.');
        })
    : Promise.resolve();

  readyPromise.then(runBackInStockRequest)
    .then((response) => {
      if(response.status === 202) {
        const defaultMessage = container.querySelector(SELECTOR_SUBMIT_MESSAGE_DEFAUL);
        const errorMessage = container.querySelector(SELECTOR_SOLDOUT_NOTIFY_ERROR);
        const successMessage = container.querySelector(SELECTOR_SUBMIT_MESSAGE_SUCCESS);
        
        if (defaultMessage) {
          defaultMessage.classList.remove(CLASS_ACTIVE);
        }
        if (errorMessage) {
          errorMessage.classList.remove(CLASS_ACTIVE);
          // Reset to default error message
          errorMessage.textContent = 'Something went wrong. Please try again.';
        }
        if (successMessage) {
          successMessage.classList.add(CLASS_ACTIVE);
        }
        
        form.reset();
        form.classList.add(CLASS_HIDDEN);
      } else {
        console.error('Unexpected response status:', response.status);
        let errorText = 'Something went wrong. Please try again.';
        
        const showErrorWithText = (text: string) => {
          const defaultMessage = container.querySelector(SELECTOR_SUBMIT_MESSAGE_DEFAUL);
          const errorMessage = container.querySelector(SELECTOR_SOLDOUT_NOTIFY_ERROR) as HTMLElement;
          const successMessage = container.querySelector(SELECTOR_SUBMIT_MESSAGE_SUCCESS);
          
          if (defaultMessage) defaultMessage.classList.remove(CLASS_ACTIVE);
          if (successMessage) successMessage.classList.remove(CLASS_ACTIVE);
          if (errorMessage) {
            const existingTimeout = (errorMessage as any)._hideTimeout;
            if (existingTimeout) {
              clearTimeout(existingTimeout);
            }
            
            errorMessage.textContent = text;
            errorMessage.classList.add(CLASS_ACTIVE);
            
            (errorMessage as any)._hideTimeout = setTimeout(() => {
              errorMessage.classList.remove(CLASS_ACTIVE);
              delete (errorMessage as any)._hideTimeout;
            }, 5000);
          }
        };
        
        response.text().then((text: string) => {
          try {
            const data = JSON.parse(text);
            if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
              const firstError = data.errors[0];
              if (firstError.detail) {
                errorText = firstError.detail;
              } else if (firstError.title) {
                errorText = firstError.title;
              } else if (typeof firstError === 'string') {
                errorText = firstError;
              }
            } else if (data.message) {
              errorText = data.message;
            } else if (data.error) {
              errorText = typeof data.error === 'string' ? data.error : data.error.message || errorText;
            }
          } catch (e) {
            console.log('Could not parse error response as JSON');
          }
          showErrorWithText(errorText);
        }).catch(() => {
          showErrorWithText(errorText);
        });
      }
    })
    .catch((err: Error & { profileUpdate?: boolean }) => {
      if (err?.profileUpdate) return;
      console.error('Fetch error caught:', err);
     
      const defaultMessage = container.querySelector(SELECTOR_SUBMIT_MESSAGE_DEFAUL);
      const errorMessage = container.querySelector(SELECTOR_SOLDOUT_NOTIFY_ERROR) as HTMLElement;
      const successMessage = container.querySelector(SELECTOR_SUBMIT_MESSAGE_SUCCESS);
      
      if (defaultMessage) defaultMessage.classList.remove(CLASS_ACTIVE);
      if (successMessage) successMessage.classList.remove(CLASS_ACTIVE);
      if (errorMessage) {
        const existingTimeout = (errorMessage as any)._hideTimeout;
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }
        
        errorMessage.textContent = 'Something went wrong. Please try again.';
        errorMessage.classList.add(CLASS_ACTIVE);

        (errorMessage as any)._hideTimeout = setTimeout(() => {
          errorMessage.classList.remove(CLASS_ACTIVE);
          delete (errorMessage as any)._hideTimeout;
        }, 5000);
      }
    });
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBackInStockForm);
} else {
  initBackInStockForm();
}

// Re-initialize when content is dynamically loaded
export function rebindBackInStockForm() {
  initBackInStockForm();
  
  // Re-bind submit buttons for dynamically loaded content
  const submitButtons = document.querySelectorAll(SELECTOR_SOLDOUT_SUBMIT_BUTTON);
  submitButtons.forEach(button => {
    if (button.hasAttribute('data-bis-submit-initialized')) return;
    
    button.addEventListener('click', onButtonSubmitSoldOutNotify);
    button.addEventListener('touchend', onButtonSubmitSoldOutNotify);
    button.setAttribute('data-bis-submit-initialized', 'true');
  });
}

